import { cn } from '@invent/wl-ui-kit-next';
import {
  Button,
  Checkbox,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle
} from '@invent/wl-ui-kit-next/ui';
import { Plus } from 'lucide-react';
import * as React from 'react';

import type {
  IListAssignee,
  IListFieldCatalogue,
  IListFormattingRule
} from '../../api/lists-contracts';
import {
  leafOf,
  newLeaf,
  writeLeaf,
  type ICriteriaLeaf,
  type ICriteriaWords,
  type KindOf
} from '../../list-criteria';
import { FORMATTING_TONES, formattingTone, formattingToneLabel } from '../../lists';
import { ConditionRow, builderFields, type IBuilderContext } from './advanced-filter';
import { formattingClass } from './cells';

/**
 * *Formatting…* (SR-230, design `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md`
 * §3.8) — a side sheet of the view's formatting rules, one row each: *field · operator · value*
 * composed as a filter's condition is (`ConditionRow`, the same controls and words), then the
 * colour, *Bold* and *Whole row*. **Applying changes the screen, not the view**: the rules are part
 * of what *Save view* saves, and until then the list is *Changed — not saved*, as a funnel leaves
 * it.
 *
 * **Nothing here judges a rule.** survey-api stores the shape and checks only that it is one (at
 * most 50, an operator of the grammar, a colour named, bold and row said) — its 400 lands on the
 * row its `formattingRules[i]` names, kept with the row through the visit. **The colours are the
 * five tones** (the owner's Q4): a migrated rule's Conga colour, stored as sent, is offered as
 * itself — "colour not carried over (#CCE5FF)" — until somebody picks a tone, so opening and
 * applying the sheet changes no rule nobody touched.
 */

/** A rule as the sheet holds it: its condition as a builder leaf, and its look. */
interface IRuleDraft {
  leaf: ICriteriaLeaf;
  tone: string;
  bold: boolean;
  row: boolean;
}

function draftOf(rule: IListFormattingRule): IRuleDraft {
  return {
    leaf: leafOf(rule.field ?? '', rule.op ?? '', rule.value ?? undefined),
    tone: rule.tone ?? FORMATTING_TONES[0].tone,
    bold: rule.bold === true,
    row: rule.row === true
  };
}

function ruleOf(draft: IRuleDraft, kindOf: KindOf): IListFormattingRule {
  const wire = writeLeaf(draft.leaf, kindOf);
  return {
    field: typeof wire.field === 'string' ? wire.field : null,
    op: typeof wire.op === 'string' ? wire.op : null,
    value: wire.value ?? null,
    tone: draft.tone,
    bold: draft.bold,
    row: draft.row
  };
}

/** survey-api's refusals of the rules, by the rule's index — `formattingRules[2].op` is rule 3's. */
export function ruleProblems(
  problems: readonly { target: string; reason: string }[]
): ReadonlyMap<number, string[]> {
  const byRule = new Map<number, string[]>();
  for (const problem of problems) {
    const at = /^formattingRules\[(\d+)\](?:\.(\w+))?/.exec(problem.target);
    if (!at) continue;
    const index = Number(at[1]);
    const said = at[2] ? `${RULE_PARTS[at[2]] ?? at[2]} ${problem.reason}` : problem.reason;
    byRule.set(index, [...(byRule.get(index) ?? []), said]);
  }
  return byRule;
}

/** A rule's members in words, for a refusal that names one. */
const RULE_PARTS: Record<string, string> = {
  field: 'The field',
  op: 'The operator',
  value: 'The value',
  tone: 'The colour',
  bold: 'Bold',
  row: 'Whole row'
};

export function FormattingSheet({
  open,
  onOpenChange,
  rules,
  catalogue,
  words,
  kindOf,
  accounts,
  problems,
  onApply
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rules: readonly IListFormattingRule[];
  catalogue: Nullable<IListFieldCatalogue>;
  words: ICriteriaWords;
  kindOf: KindOf;
  accounts?: readonly IListAssignee[];
  /** survey-api's refusals of the rules as last saved, by index (`ruleProblems`). */
  problems: ReadonlyMap<number, string[]>;
  onApply: (rules: IListFormattingRule[]) => void;
}) {
  const [drafts, setDrafts] = React.useState<IRuleDraft[]>([]);
  const [touched, setTouched] = React.useState(false);
  const [held, setHeld] = React.useState(problems);
  // The rows start again from the view's rules on each opening, and only then — the Advanced
  // sheet's rule: a poll re-rendering the screen must not take the rows being composed.
  const [wasOpen, setWasOpen] = React.useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setDrafts(rules.map(draftOf));
      setTouched(false);
      setHeld(problems);
    }
  }

  const { fields, sections } = React.useMemo(
    () => builderFields(catalogue, new Set(drafts.map((draft) => draft.leaf.field))),
    [catalogue, drafts]
  );
  // A refusal belongs to the rule it named as last saved — said until the rows are touched, since
  // an index means nothing once a row is added or removed.
  const byId = React.useMemo(() => {
    const map = new Map<string, string[]>();
    if (!touched) drafts.forEach((draft, index) => map.set(draft.leaf.id, held.get(index) ?? []));
    return map;
  }, [drafts, held, touched]);
  const context: IBuilderContext = {
    fields,
    sections,
    words,
    accounts,
    survey: catalogue?.survey ?? null,
    paths: new Map(drafts.map((draft, index) => [draft.leaf.id, `formattingRules[${index}]`])),
    numbers: new Map(drafts.map((draft, index) => [draft.leaf.id, index + 1])),
    problemsOf: (id) => byId.get(id) ?? []
  };

  const edit = (next: IRuleDraft[]) => {
    setTouched(true);
    setDrafts(next);
  };
  const editAt = (index: number, change: Partial<IRuleDraft>) =>
    edit(drafts.map((draft, at) => (at === index ? { ...draft, ...change } : draft)));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[min(52rem,calc(100vw-2rem))] gap-0 overflow-y-auto p-0">
        <div className="grid gap-1 border-b border-border px-5 py-4">
          <SheetTitle className="text-[0.9375rem] text-[var(--color-royal-blue)]">Formatting</SheetTitle>
          <SheetDescription className="text-[0.8125rem]">
            A cell — or its whole row — that meets a rule takes the rule’s colour. The first rule
            a cell meets wins. Kept with the view, and saved with it.
          </SheetDescription>
        </div>
        <form
          className="grid gap-4 px-5 py-4 text-[0.8125rem]"
          onSubmit={(event) => {
            event.preventDefault();
            // Rows nobody touched are the rules as read: an applied sheet changes nothing then.
            if (touched) onApply(drafts.map((draft) => ruleOf(draft, kindOf)));
            onOpenChange(false);
          }}
        >
          {drafts.length === 0 && <p className="text-muted-foreground">This view colours nothing.</p>}
          {drafts.map((draft, index) => (
            <ConditionRow
              key={draft.leaf.id}
              leaf={draft.leaf}
              context={context}
              noun="Rule"
              hook="data-rule"
              onChange={(leaf) => editAt(index, { leaf })}
              onRemove={() => edit(drafts.filter((_, at) => at !== index))}
            >
              <ToneSelect
                label={`Rule ${index + 1} — Colour`}
                tone={draft.tone}
                stored={rules[index]?.tone ?? null}
                onTone={(tone) => editAt(index, { tone })}
              />
              <label className="flex h-8 items-center gap-1.5">
                <Checkbox
                  checked={draft.bold}
                  onCheckedChange={(checked) => editAt(index, { bold: checked === true })}
                />
                Bold
              </label>
              <label className="flex h-8 items-center gap-1.5">
                <Checkbox
                  checked={draft.row}
                  onCheckedChange={(checked) => editAt(index, { row: checked === true })}
                />
                Whole row
              </label>
            </ConditionRow>
          ))}
          <Button
            type="button"
            variant="outline"
            className="h-8 w-fit gap-1.5 px-2.5 font-normal"
            onClick={() =>
              edit([...drafts, { leaf: newLeaf(), tone: FORMATTING_TONES[0].tone, bold: false, row: false }])
            }
          >
            <Plus className="size-4" aria-hidden />
            Add a rule
          </Button>
          <div className="flex gap-2 border-t border-border pt-3">
            <Button type="submit" className="h-8 px-3">
              Apply
            </Button>
            <Button type="button" variant="outline" className="h-8 px-3" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}

/** The five tones, and a migrated rule's Conga colour as itself while it is the rule's. */
function ToneSelect({
  label,
  tone,
  stored,
  onTone
}: {
  label: string;
  tone: string;
  stored: Nullable<string>;
  onTone: (tone: string) => void;
}) {
  const uncarried = [tone, stored].filter(
    (one, index, all): one is string =>
      one !== null && !formattingTone(one).carried && all.indexOf(one) === index
  );
  return (
    <Select value={tone} onValueChange={onTone}>
      <SelectTrigger aria-label={label} className="h-8 w-44">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {FORMATTING_TONES.map((one) => (
          <SelectItem key={one.tone} value={one.tone}>
            <span className="flex items-center gap-2">
              <span
                aria-hidden
                className={cn('h-3.5 w-5 rounded-sm border border-border', formattingClass({ tone: one.tone, bold: false }))}
              />
              {one.label}
            </span>
          </SelectItem>
        ))}
        {uncarried.map((one) => (
          <SelectItem key={one} value={one}>
            {formattingToneLabel(one)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
